import { useEffect, useState } from 'react';
import { CheckCircle2, ExternalLink, HardDrive, Loader2, X } from 'lucide-react';
import { commanderModulesAdmin, fetchSauvegardes } from '../services/api';

/** Les octets du serveur, en unités lisibles. */
function lisible(octets: number): string {
  const unites = ['o', 'Ko', 'Mo', 'Go'];
  let rang = 0;
  let valeur = octets;
  while (valeur >= 1024 && rang < unites.length - 1) {
    valeur /= 1024;
    rang += 1;
  }
  return `${valeur.toLocaleString('fr-FR', { maximumFractionDigits: valeur >= 100 || rang === 0 ? 0 : 1 })} ${unites[rang]}`;
}

/**
 * La fenêtre qui propose l'extension d'espace.
 *
 * Elle s'ouvre là où la limite se rencontre — au moment d'un dépôt refusé, ou
 * d'un clic sur le badge — plutôt que d'envoyer chercher une page ailleurs.
 * On y voit ce qui est occupé, ce que l'extension apporte, et les deux façons
 * de la prendre.
 *
 * Le règlement se fait d'ici : l'extension est une option de la plateforme
 * comme les autres, et emprunte le même chemin que la boutique de goodies ou
 * le coffre-fort. On ne quitte le site que pour la page de paiement de la
 * banque — la seule étape qui ne puisse pas se passer ailleurs que chez elle.
 *
 * Le lien vers la boutique reste offert à côté : un trésorier préfère parfois
 * voir l'offre entière avant d'engager son association.
 */
export default function ModaleEspace({
  ouverte,
  onFermer,
  message,
}: {
  ouverte: boolean;
  onFermer: () => void;
  message?: string | null;
}) {
  const [etat, setEtat] = useState<any | null>(null);
  const [periode, setPeriode] = useState<'mois' | 'an'>('an');
  const [chargement, setChargement] = useState(false);
  const [commande, setCommande] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  /**
   * Ouvre le paiement de l'extension.
   *
   * Même chemin que les autres options de la plateforme : la commande part
   * vers la boutique, qui rend une adresse de paiement. L'espace s'ouvre au
   * retour de la banque, sans intervention.
   */
  const commander = async () => {
    setCommande(true);
    setErreur(null);
    try {
      const reponse: any = await commanderModulesAdmin(
        ['espace-supplementaire'],
        periode === 'an' ? 'annuel' : 'mensuel'
      );
      if (reponse?.url) {
        window.location.href = reponse.url;
        return;
      }
      setErreur(reponse?.message ?? "La commande n'a pas abouti.");
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'La commande a échoué.');
    } finally {
      setCommande(false);
    }
  };

  useEffect(() => {
    if (!ouverte) return;
    setChargement(true);
    fetchSauvegardes()
      .then((d: any) => setEtat(d))
      .catch(() => setEtat(null))
      .finally(() => setChargement(false));
  }, [ouverte]);

  if (!ouverte) return null;

  const documents = etat?.quotas?.documents;
  const base = etat?.quotas?.base;
  const forfait = etat?.forfait;
  const dejaEtendu = etat?.espace?.etendu === true;

  const lienBoutique = 'https://shop.gest-asso.com/';

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onFermer}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
              <HardDrive className="w-5 h-5" />
            </span>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {dejaEtendu ? 'Votre espace étendu' : "Étendre votre espace"}
              </h2>
              <p className="text-xs text-gray-500">Sans rien effacer, et sans interruption.</p>
            </div>
          </div>
          <button onClick={onFermer} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {message && (
          <p className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
            {message}
          </p>
        )}

        {chargement ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            {documents && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-4 space-y-3">
                <Jauge libelle="Documents" utilise={documents.utilise} total={documents.total} />
                {base && <Jauge libelle="Base de données" utilise={base.utilise} total={base.total} />}
              </div>
            )}

            {dejaEtendu ? (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                L'extension est déjà active sur votre instance. Si l'espace vient encore à manquer,
                écrivez-nous : au-delà de ce forfait, la place se règle au cas par cas.
              </p>
            ) : (
              <>
                {forfait && (
                  <div className="rounded-xl border-2 border-primary-200 bg-primary-50/50 dark:bg-primary-900/10 p-4 mb-4">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Ce que l'extension apporte
                    </p>
                    <ul className="space-y-1.5 text-sm text-gray-700 dark:text-gray-300">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                        Base de données portée à {lisible(forfait.base)}
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                        Documents portés à {lisible(forfait.documents)}
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                        Rien à effacer, rien à réinstaller — l'espace s'ouvre au règlement
                      </li>
                    </ul>

                    <div className="flex gap-2 mt-4">
                      {([
                        ['an', `${forfait.prixAnnuel} € / an`, 'deux mois offerts'],
                        ['mois', `${forfait.prixMensuel} € / mois`, 'sans engagement'],
                      ] as const).map(([code, prix, note]) => (
                        <button
                          key={code}
                          onClick={() => setPeriode(code)}
                          className={`flex-1 rounded-lg border-2 px-3 py-2 text-left transition ${
                            periode === code
                              ? 'border-primary-600 bg-white dark:bg-gray-800'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {prix}
                          </span>
                          <span className="block text-xs text-gray-500">{note}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {erreur && (
                  <p className="mb-3 rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-800">
                    {erreur}
                  </p>
                )}

                <button
                  onClick={commander}
                  disabled={commande}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-60"
                >
                  {commande && <Loader2 className="w-5 h-5 animate-spin" />}
                  Régler{' '}
                  {periode === 'an'
                    ? `${forfait?.prixAnnuel ?? 100} € pour l'année`
                    : `${forfait?.prixMensuel ?? 10} € par mois`}
                </button>

                <p className="mt-3 text-xs text-gray-500 text-center">
                  Paiement sécurisé par carte. L'espace s'ouvre dès le règlement encaissé, sans
                  rien à réinstaller.
                </p>

                <a
                  href={lienBoutique}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 w-full inline-flex items-center justify-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
                >
                  Voir l'offre sur la boutique
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Jauge({ libelle, utilise, total }: { libelle: string; utilise: number; total: number }) {
  const part = total > 0 ? Math.min(100, (utilise / total) * 100) : 0;
  const depasse = utilise > total;
  const couleur = depasse || part >= 95 ? 'bg-red-500' : part >= 85 ? 'bg-amber-500' : 'bg-primary-600';

  return (
    <div>
      <div className="flex items-baseline justify-between text-sm mb-1">
        <span className="text-gray-700 dark:text-gray-300">{libelle}</span>
        <span className={`tabular-nums ${depasse ? 'text-red-700 font-medium' : 'text-gray-500'}`}>
          {lisible(utilise)} / {lisible(total)}
        </span>
      </div>
      <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
        <div className={`h-full ${couleur}`} style={{ width: `${part}%` }} />
      </div>
    </div>
  );
}
